import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../utils/api';
import { useAuth } from './AuthContext';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [] });
  const [sessionId, setSessionId] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);
  const { user } = useAuth();

  useEffect(() => {
    let storedSessionId = localStorage.getItem('sessionId');
    if (!storedSessionId) {
      storedSessionId = `session-${Date.now()}-${Math.random()}`;
      localStorage.setItem('sessionId', storedSessionId);
    }
    setSessionId(storedSessionId);
    fetchCart(storedSessionId);
  }, [user]);

  const fetchCart = async (sid) => {
    try {
      const params = user ? {} : { session_id: sid };
      const response = await api.get('/cart', { params });
      setCart(response.data);
    } catch (error) {
      console.error('Failed to fetch cart:', error);
    } finally {
      setCartLoading(false);
    }
  };

  const addToCart = async (product, quantity = 1, customization = null) => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await api.post('/cart/add', {
        product_id: product.id,
        quantity,
        customization
      }, { params });
      await fetchCart(sessionId);
    } catch (error) {
      console.error('Failed to add to cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (productId) => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await api.delete(`/cart/${productId}`, { params });
      await fetchCart(sessionId);
    } catch (error) {
      console.error('Failed to remove from cart:', error);
    }
  };

  const clearCart = async () => {
    try {
      const params = user ? {} : { session_id: sessionId };
      await api.delete('/cart', { params });
      setCart({ items: [] });
    } catch (error) {
      console.error('Failed to clear cart:', error);
    }
  };

  return (
    <CartContext.Provider value={{ cart, addToCart, removeFromCart, clearCart, sessionId, cartLoading }}>
      {children}
    </CartContext.Provider>
  );
};